// Gemeinsame Schicht: die Bausteine, die auf MEHREREN Seiten sitzen und
// deshalb keinem Bereich allein gehören — Projekt-Reiter, Projektliste der
// Sidebar, Flotten-Schalter, Instruktionen-/Wissen-Panel, das Home-Eingabefeld,
// die Personen-Suche, der Intake-Zustandschip, die Terminliste im Sidebar-Kopf,
// die Home-Chips, die Befehls-Palette und der App-Rahmen.
//
// Deutsch ist die QUELLE; ../en/shared.ts spiegelt diesen Typ.
export const shared = {
  // ── Projektliste der Sidebar (components/ProjectsSidebarTree.tsx) ────────
  // Steht auf JEDER Seite (App-Rahmen), gehört deshalb hierher und nicht in
  // `projects` — das ist der Katalog der Projekt-SEITE.
  projekteBaum: {
    leer: "Noch keine Projekte. Über die Projekte-Seite anlegen.",
    neuesProjekt: "Neues Projekt",
    umbenennen: "Projekt umbenennen",
    umbenennenFehlgeschlagen: "Projekt-Umbenennen fehlgeschlagen",
    nameFeldAria: "Projekt-Name",
    zeileTitel: "Klick: oeffnen · Doppelklick: umbenennen",
    // Eigener Key statt `common.schliessen`: der deutsche Text lautet hier
    // „Schliessen" (ohne ß) und deutsche Strings werden beim Übersetzen nicht
    // umgeschrieben (docs/i18n.md §4 Regel 3).
    schliessen: "Schliessen",
  },

  // ── Befehls-Palette (components/CommandPalette.tsx) ──────────────────────
  // Erreichbar per Alt+Q und über die Lupe — also auf JEDER Seite, deshalb hier
  // und nicht im Katalog eines Bereichs.
  //
  // EIGENE Keys, auch wo ein Eintrag dasselbe Ziel wie die Navigation nennt:
  // `nav` gehört der Sidebar (anderer Namespace), und ein geteilter Key würde
  // die Palette an eine fremde Beschriftung koppeln. Bewusst in Kauf genommene
  // Duplikate (in BEIDEN Sprachen wortgleich):
  //   aktionen.uebersicht → nav.uebersicht · aktionen.notizen → nav.notizen
  //   aktionen.projekte   → nav.projekte   · aktionen.tabellen → nav.tabellen
  //   aktionen.dokumentHochladen → shared.useCases.upload.chip
  palette: {
    platzhalter: "Suche, Aktion, Sprung …",
    // Die beiden Auslöser im App-Rahmen (components/Layout.tsx): Lupe im
    // Sidebar-Kopf und in der Mobil-Kopfzeile. Die TASTE im Titel kommt aus
    // mail/shortcuts.ts und bleibt unübersetzt — eine übersetzte Taste wäre
    // eine, die es nicht gibt.
    oeffnen: "Befehls-Palette öffnen",
    knopfTitel: (taste: string) => `Befehls-Palette (${taste})`,
    gruppeAktionen: "Aktionen",
    // Schließendes Anführungszeichen wörtlich wie im Bestand (gerade Doppel-
    // anführung) — deutsche Strings werden beim Umbau nicht umgeschrieben.
    keineTreffer: (suche: string) => `Keine Treffer für „${suche}".`,
    fussNavigieren: "navigieren",
    fussOeffnen: "öffnen",
    fussSchliessen: "schließen",

    aktionen: {
      quickNote: "Quick-Capture: neue Notiz",
      uebersicht: "Übersicht",
      notizen: "Notizen",
      frageStellen: "Frage stellen",
      dokumentHochladen: "Dokument hochladen",
      projekte: "Projekte",
      zeitachse: "Zeitachse",
      tabellen: "Tabellen",
      mailSuche: "Mail: Suche fokussieren",
      mailTermin: "Mail: Termin aus dieser Mail",
      mailKi: "Mail: KI-Antwort vorschlagen",
      mailVorlage: "Mail: Vorlage einfügen",
      // „Salesforce" ist ein Produktname und bleibt in beiden Sprachen stehen.
      mailSf: "Mail: Frag Salesforce",
      mailKuerzel: "Mail: Tastenkürzel",
      kalender: "Kalender öffnen",
      kalenderTermin: "Kalender: Termin finden",
    },

    // Überschriften der Treffer-Gruppen. Die Keys sind hier ausnahmsweise NICHT
    // deutsch benannt: es sind die `type`-Werte aus /instant, mit denen die
    // Komponente nachschlägt — ein deutscher Key bräuchte eine zweite Map.
    trefferTyp: {
      note: "Notiz",
      task: "Aufgabe",
      document: "Dokument",
      sf_project: "SF-Projekt",
      sf_action: "SF-Action",
      chat_message: "Chat",
    },
  },

  // ── App-Rahmen (components/Layout.tsx) ───────────────────────────────────
  // Nur die Beschriftungen, die NICHT zur Navigation gehören (die steht in
  // `nav`) und keinem Bereich zuzuordnen sind.
  rahmen: {
    profilMenue: "Profil-Menü",
    // Bestandsschreibweise ohne ß beibehalten (Walk-Zusage, i18n.md §4 Regel 3).
    sidebarSchliessen: "Sidebar schliessen",
    // Dasselbe in Grün wie oben: der Auslöser des Burger-Menüs schreibt sich im
    // Bestand „oeffnen".
    sidebarOeffnen: "Sidebar oeffnen",

    // ── Inhalt des Profil-Menüs ────────────────────────────────────────────
    // Der AUSLÖSER (`profilMenue`) war übersetzt, sein Inhalt nicht — genau das
    // Muster, das docs/i18n.md §4 Regel 2 verbietet.
    // `einstellungen` ist wortgleich mit `settings.kopfTitel` (der Überschrift
    // der Zielseite): eigener Key, weil Menü-Eintrag und Seiten-Überschrift
    // unabhängig voneinander umbenannt werden können.
    statistik: "Statistik",
    einstellungen: "Einstellungen",
    abmelden: "Abmelden",

    // ── Hell-/Dunkel-Schalter ──────────────────────────────────────────────
    // Beide Beschriftungen nennen das ZIEL des Klicks, nicht den Ist-Zustand:
    // im dunklen Modus steht „Hell" am Knopf. Welcher Key gilt, entscheidet der
    // Code (`effective`) — Zustände gehören nicht in den Katalog (§3).
    // Wortgleich mit `settings.modus.light/dark` (Anzeige-Reiter); dort ist es
    // eine Auswahl-Liste mit drei Werten, hier ein Zwei-Zustands-Schalter.
    designHell: "Hell",
    designDunkel: "Dunkel",
    designHellAria: "Helles Design",
    designDunkelAria: "Dunkles Design",
  },

  // ── Reiter der Projekt-Seite (components/ProjectTabs.tsx) ────────────────
  projektReiter: {
    chats: "Chats",
    aufgaben: "Aufgaben",
    artefakte: "Artefakte",
    // „Intake" ist der Name des Verfahrens (Rohsprache → Board) und in beiden
    // Sprachen dasselbe Wort.
    intake: "Intake",
  },

  // ── Flotten-Schalter (components/FleetToggle.tsx) ────────────────────────
  flotte: {
    titel: "Flotte",
    aktiv: "aktiv",
    schalterAria: "Flotte für dieses Projekt an- oder ausschalten",
    titelAn:
      "Fleet aktiv — die Loop-Flotte darf für dieses Projekt Loops starten. Klicken zum Ausschalten.",
    titelAus: "Fleet aus — Klicken, damit die Loop-Flotte dieses Projekt bearbeitet.",
  },

  // ── Instruktionen-Panel (components/InstructionsPanel.tsx) ───────────────
  instruktionen: {
    titel: "Instruktionen",
    platzhalter: "z.B. 'Antworte formell. Beziehe interne Studien priorisiert mit ein.'",
    laedt: "Lade …",
    feldAria: "Instruktionen fuer dieses Projekt",
    feldTitel: "Wird vor jeden System-Prompt gestellt.",
    gespeichert: "Instruktionen gespeichert",
  },

  // ── Wissen-Panel (components/KnowledgePanel.tsx) ─────────────────────────
  wissen: {
    titel: "Wissen",
    dateiKnopf: "Datei",
    dateiHinzufuegen: "Datei hinzufügen",
    dateiFeldAria: "Wissens-Datei (PDF/Markdown) hochladen",
    ablage: "PDF/MD hierher ziehen oder Plus klicken",
    summaryLaeuft: "Summary wird generiert …",
    loeschenFrage: (titel: string) => `„${titel}" löschen?`,
    hochgeladen: "Hochgeladen",
    uploadFehlgeschlagen: "Upload fehlgeschlagen",
    loeschenFehlgeschlagen: "Löschen fehlgeschlagen",
    externeQuellen: "Externe Quellen",
  },

  // ── Home-Eingabefeld (components/UnifiedComposer.tsx) ────────────────────
  composer: {
    platzhalter: "Womit kann Atlas helfen?",
    feldAria: "Frage stellen oder Notiz festhalten",
    optionen: "Optionen — Projekt, Recherche, Anhang",
    optionenSchliessen: "Optionen schließen",
    zielProjekt: "Ziel-Projekt",
    // Anzeige-Name für „kein Projekt gewählt". Der API-Wert ist die leere
    // Projekt-Id, nicht dieser Text.
    allgemein: "Allgemein",
    dokumentAnhaengen: "Dokument anhängen",
    // Dateiformate — in beiden Sprachen dieselben Kürzel.
    dateiTypen: "PDF · MD · Text",
    alsNotizSpeichern: "Als Notiz speichern",
    senden: "Senden",
    frageFehlgeschlagen: "Frage fehlgeschlagen",
    notizGespeichert: "Notiz gespeichert",
  },

  // ── Personen-Suche (components/UserPicker.tsx) ───────────────────────────
  personen: {
    platzhalter: "Mitarbeiter suchen …",
    entfernen: (name: string) => `${name} entfernen`,
    laedt: "lädt …",
    keineTreffer: "keine Treffer",
    keineTrefferFuer: (suche: string) => `keine Treffer für „${suche}"`,
  },

  // ── Intake-Zustand (components/intake/IntakeStatusChip.tsx) ──────────────
  intakeStatus: {
    raw: "Roh",
    questions: "Fragen offen",
    answered: "Beantwortet",
    planning: "In Planung",
    planned: "Geplant",
  },

  // ── Terminliste im Sidebar-Kopf (components/NextMeetingChip.tsx) ─────────
  // Die UHRZEITEN selbst bleiben deutsch formatiert (docs/i18n.md §2); hier
  // stehen nur die Beschriftungen drumherum.
  termine: {
    standUm: (zeit: string) => `Stand ${zeit}`,
    standUnbekannt: "Stand —",
    nichtErreichbar: " · nicht erreichbar",
    aktualisieren: "Termine aktualisieren",
    keineTermine: (tage: number) => `Keine Termine in den nächsten ${tage} Tagen`,
    listeAria: (tage: number) => `Termine der nächsten ${tage} Tage`,
    naechsterTermin: (betreff: string, wann: string) => `Nächster Termin: ${betreff} — ${wann}`,
    beitreten: "Beitreten",
    // Countdown-/Tages-Beschriftungen. Sie werden an die katalogfreien
    // Funktionen in components/nextMeeting.ts durchgereicht — die Datei bleibt
    // importfrei, damit x2-label-dryrun.mts sie weiter laden kann.
    laeuft: "läuft",
    inMin: (n: number) => `in ${n} Min`,
    umUhr: (zeit: string) => `um ${zeit}`,
    heute: "Heute",
    morgen: "Morgen",
  },

  // ── Use-Case-Registry (useCases.ts) ──────────────────────────────────────
  // Chip-Kurzlabel + Erklärsatz (Tooltip) je Eintrag. „Atlas", „Blog" und
  // „Video Studio" sind Produktnamen und bleiben stehen.
  useCases: {
    ask: {
      titel: "Wissen fragen",
      beschreibung: "Stelle Fragen an Dokumente und Projektwissen.",
    },
    upload: {
      titel: "Dokument hochladen & ausfragen",
      beschreibung: "Hänge ein PDF an und befrage seinen Inhalt direkt im Chat.",
      chip: "Dokument hochladen",
    },
    note: {
      titel: "Notiz festhalten",
      beschreibung: "Halte einen Gedanken in Sekunden fest.",
      chip: "Neue Notiz",
    },
    tasks: {
      titel: "Meine Aufgaben",
      beschreibung: "Sieh, was ansteht — Kanban, Kalender, Zeitachse.",
      chip: "Meine Aufgaben",
    },
    appointment: {
      titel: "Termin finden",
      beschreibung: "Finde freie Zeitfenster für ein Meeting.",
      chip: "Termin",
    },
    email: {
      titel: "E-Mails & Antworten",
      beschreibung: "Fasse Postfach-Threads zusammen und entwirf Antworten.",
    },
    blog: {
      titel: "Blog-Artikel erstellen",
      beschreibung: "Erzeuge fundierte Fachartikel aus deinem Wissen.",
      chip: "Blog-Artikel",
    },
    video: {
      titel: "Video erstellen",
      beschreibung: "Produziere Avatar-Videos aus einem Skript.",
      chip: "Video",
    },
    claims: {
      titel: "Claims prüfen",
      beschreibung: "Prüfe Werbeaussagen auf Compliance und Belege.",
    },
    datasets: {
      titel: "Tabellen aus Dokumenten",
      beschreibung: "Extrahiere strukturierte Tabellen aus Dokumenten.",
    },
  },
};

export type SharedKatalog = typeof shared;
